import type { CSSProperties, ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import { useNow } from '@/hooks/useNow';
import { formatDateLong } from '@/lib/format';
import { routes } from '@/lib/routes';

type RailLinks = {
  notebook?: string;
  calendar?: string;
  meetings?: string;
  connections?: string;
  alliances?: string;
  qa?: string;
  support?: string;
  trajectory?: string;
};

type Props = {
  children: ReactNode;
  links?: RailLinks;
  /** Hide rails on nested module screens if needed. Default true. */
  showRails?: boolean;
};

const DEFAULT_LINKS: Required<RailLinks> = {
  notebook: routes.profileSection('documents'),
  calendar: routes.newRequest,
  meetings: routes.portalSection('education'),
  connections: routes.people,
  alliances: routes.people,
  qa: routes.portalSection('instructions'),
  support: routes.portalSection('contacts'),
  trajectory: routes.researchTools,
};

/** Shared 3-zone cabinet body: left rails | center | right rails (Task 14 §2). */
export const CabinetWorkspaceFrame = ({ children, links, showRails = true }: Props) => {
  const navigate = useNavigate();
  const now = useNow();
  const href = { ...DEFAULT_LINKS, ...links };

  if (!showRails) return <>{children}</>;

  return (
    <section className="dashboard ov-premium-owner role-cabinet-frame">
      <div className="ov-home-grid">
        <div className="ov-left-premium">
          <section className="ov-card ov-notebook" style={{ '--a': '#9e80ff' } as CSSProperties}>
            <header>
              <div className="ov-kicker">Личное пространство</div>
              <div className="ov-title-row">
                <div>
                  <h3>Академический блокнот</h3>
                  <p>Источники, идеи, evidence и стратегические решения.</p>
                </div>
                <span className="ov-icon" aria-hidden="true">
                  <Icon name="document" size={22} />
                </span>
              </div>
            </header>
            <div className="ov-body">
              <div className="ov-metrics four">
                <div>
                  <small>Последнее сохранение</small>
                  <b>сегодня</b>
                </div>
                <div>
                  <small>Активные записи</small>
                  <b>12</b>
                </div>
                <div>
                  <small>Черновики</small>
                  <b>3</b>
                </div>
                <div>
                  <small>Избранное</small>
                  <b>5</b>
                </div>
              </div>
              <div className="ov-note-row">
                <strong>Рабочие заметки</strong>
                <span>Черновик: критерии отбора журнала Q1–Q2</span>
              </div>
              <div className="ov-actions">
                <button type="button" onClick={() => navigate(href.notebook)}>
                  Открыть блокнот
                </button>
                <button type="button" onClick={() => navigate(href.notebook)}>
                  + Добавить запись
                </button>
              </div>
            </div>
          </section>

          <section className="ov-card ov-calendar" style={{ '--a': '#e2ad3a' } as CSSProperties}>
            <header>
              <div className="ov-kicker">{formatDateLong(now, 'ru')}</div>
              <div className="ov-title-row">
                <div>
                  <h3>Служебный календарь</h3>
                  <p>Сроки заявок, публикаций, проектов и встреч.</p>
                </div>
                <span className="ov-icon" aria-hidden="true">
                  <Icon name="calendar" size={22} />
                </span>
              </div>
            </header>
            <div className="ov-body">
              <div className="ov-metrics two">
                <div>
                  <small>Срочные события</small>
                  <b>2</b>
                </div>
                <div>
                  <small>Ближайший срок</small>
                  <b>пн</b>
                </div>
              </div>
              <div className="ov-note-row">
                <strong>Ближайшее</strong>
                <span>Дедлайн комплекта материалов · пн 10:00</span>
              </div>
              <div className="ov-actions">
                <button type="button" onClick={() => navigate(href.calendar)}>
                  Открыть календарь
                </button>
                <button type="button" onClick={() => navigate(href.meetings)}>
                  + Добавить событие
                </button>
              </div>
            </div>
          </section>

          <section className="ov-card" style={{ '--a': '#41d2ff' } as CSSProperties}>
            <header>
              <div className="ov-kicker">Human review</div>
              <div className="ov-title-row">
                <div>
                  <h3>Вопрос–ответ</h3>
                  <p>Официальная база знаний и ответы специалистов.</p>
                </div>
                <span className="ov-icon" aria-hidden="true">
                  <Icon name="info" size={22} />
                </span>
              </div>
            </header>
            <div className="ov-body">
              <div className="ov-note-row">
                <strong>База инструкций</strong>
                <span>Ответы проверяются человеком, не AI</span>
              </div>
              <div className="ov-actions single">
                <button type="button" onClick={() => navigate(href.qa)}>
                  Открыть Q&A
                </button>
              </div>
            </div>
          </section>

          <section className="ov-card" style={{ '--a': '#9e80ff' } as CSSProperties}>
            <header>
              <div className="ov-kicker">Human support</div>
              <div className="ov-title-row">
                <div>
                  <h3>Служба поддержки</h3>
                  <p>Личный ticket, ответ и история обращений.</p>
                </div>
                <span className="ov-icon" aria-hidden="true">
                  <Icon name="chat" size={22} />
                </span>
              </div>
            </header>
            <div className="ov-body">
              <div className="ov-note-row">
                <strong>Поддержка платформы</strong>
                <span>Обрабатывается службой поддержки, не AI</span>
              </div>
              <div className="ov-actions single">
                <button type="button" onClick={() => navigate(href.support)}>
                  Открыть Support
                </button>
              </div>
            </div>
          </section>
        </div>

        <div className="ov-center-col">{children}</div>

        <div className="ov-right-premium">
          <section className="ov-card ov-events" style={{ '--a': '#ff6e56' } as CSSProperties}>
            <header>
              <div className="ov-kicker">Ближайшие действия</div>
              <div className="ov-title-row">
                <div>
                  <h3>Мероприятия и действия</h3>
                  <p>Срок, приоритет и статус каждой работы.</p>
                </div>
                <span className="ov-icon" aria-hidden="true">
                  <Icon name="trophy" size={22} />
                </span>
              </div>
            </header>
            <div className="ov-body">
              <div className="ov-note-row">
                <strong>Семинар кафедры</strong>
                <span>чт · онлайн · подтвердить участие</span>
              </div>
              <div className="ov-note-row">
                <strong>Сдача отчёта</strong>
                <span>пт · внутренний дедлайн</span>
              </div>
              <button type="button" className="ea117-launch" onClick={() => navigate(href.meetings)}>
                Открыть все мероприятия →
              </button>
            </div>
          </section>

          <button
            type="button"
            className="ov-card ov-side-entry"
            style={{ '--a': '#41d2ff' } as CSSProperties}
            onClick={() => navigate(href.connections)}
            aria-label="Открыть академические связи"
          >
            <header>
              <div className="ov-kicker">Коммуникации</div>
              <div className="ov-title-row">
                <div>
                  <h3>Академические связи</h3>
                  <p>Контакты, диалоги и совместные материалы.</p>
                </div>
                <span className="ov-icon" aria-hidden="true">
                  <Icon name="chat" size={22} />
                </span>
              </div>
            </header>
            <div className="ov-body">
              <div className="ov-note-row">
                <span>3 непрочитанных · 12 активных диалогов</span>
              </div>
              <span className="ea117-launch">Открыть все связи →</span>
            </div>
          </button>

          <button
            type="button"
            className="ov-card ov-side-entry"
            style={{ '--a': '#9e80ff' } as CSSProperties}
            onClick={() => navigate(href.alliances)}
            aria-label="Открыть альянсы"
          >
            <header>
              <div className="ov-kicker">Академические группы</div>
              <div className="ov-title-row">
                <div>
                  <h3>Альянсы</h3>
                  <p>Группы, совместные проекты и научные сети.</p>
                </div>
                <span className="ov-icon" aria-hidden="true">
                  <Icon name="graduation" size={22} />
                </span>
              </div>
            </header>
            <div className="ov-body">
              <div className="ov-note-row">
                <span>2 альянса · 1 приглашение</span>
              </div>
              <span className="ea117-launch">Открыть все альянсы →</span>
            </div>
          </button>
        </div>
      </div>
    </section>
  );
};

type GpsMilestone = {
  label: string;
  year: string;
  state: 'done' | 'now' | 'future';
};

type GpsProps = {
  openTo?: string;
  milestones?: GpsMilestone[];
  pipelineLabel?: string;
  next90Label?: string;
  focusLabel?: string;
  evidenceLabel?: string;
  progressPercent?: number;
};

const DEFAULT_MILESTONES: GpsMilestone[] = [
  { label: 'Старт', year: '2024', state: 'done' },
  { label: 'Исследователь', year: '2025', state: 'done' },
  { label: 'Лидер проекта', year: '2026', state: 'now' },
  { label: 'Эксперт', year: '2027', state: 'future' },
];

/**
 * Full Scientific GPS under cabinet work cards (Task 14 §2):
 * timeline · publication pipeline · goals · next 90 days · progress · evidence tips.
 */
export const ScientificGpsBlock = ({
  openTo,
  milestones = DEFAULT_MILESTONES,
  pipelineLabel = '2 рукописи · 1 на рецензии',
  next90Label = 'Подать в Q1–Q2 · закрыть пробелы evidence',
  focusLabel = 'Публикационный план кабинета',
  evidenceLabel = 'Добавить DOI / Scopus / ORCID к ключевым работам',
  progressPercent = 62,
}: GpsProps) => {
  const navigate = useNavigate();
  const target = openTo || routes.researchTools;

  return (
    <section
      className="ov-card ov-trajectory v17-center-trajectory"
      style={{ '--a': '#49ddb7' } as CSSProperties}
      aria-label="Моя научная траектория"
    >
      <header>
        <div className="ov-kicker">Scientific GPS</div>
        <div className="ov-title-row">
          <div>
            <h3>Моя научная траектория</h3>
            <p>
              Timeline, publication pipeline, цели и evidence-linked рекомендации на ближайшие 90
              дней.
            </p>
          </div>
          <span className="ov-icon" aria-hidden="true">
            <Icon name="chart" size={22} />
          </span>
        </div>
      </header>
      <div className="ov-body">
        <div className="ov-track" role="list">
          {milestones.map((item) => (
            <div key={`${item.label}-${item.year}`} className={item.state} role="listitem">
              <i aria-hidden="true">{item.state === 'done' ? '✓' : item.state === 'now' ? '◆' : '◇'}</i>
              <b>{item.label}</b>
              <span>{item.year}</span>
            </div>
          ))}
        </div>

        <div className="gps-progress-row" aria-label={`Прогресс ${progressPercent}%`}>
          <div className="gps-progress-meta">
            <strong>Прогресс траектории</strong>
            <span>{progressPercent}%</span>
          </div>
          <div className="gps-progress-bar">
            <i style={{ width: `${Math.max(0, Math.min(100, progressPercent))}%` }} />
          </div>
        </div>

        <div className="gps-panel-grid">
          <div className="gps-panel">
            <small>Publication pipeline</small>
            <b>{pipelineLabel}</b>
          </div>
          <div className="gps-panel">
            <small>Next 90 days</small>
            <b>{next90Label}</b>
          </div>
          <div className="gps-panel">
            <small>Goals &amp; milestones</small>
            <b>{focusLabel}</b>
          </div>
          <div className="gps-panel">
            <small>Evidence-linked</small>
            <b>{evidenceLabel}</b>
          </div>
        </div>

        <div className="ov-actions single">
          <button type="button" onClick={() => navigate(target)}>
            Открыть траекторию
          </button>
        </div>
      </div>
    </section>
  );
};

/** Convenience re-export for deep-links inside rails. */
export const CabinetRailLink = Link;
